/**
 * Quantity Magnifier Utilities
 *
 * Handles quantity magnification/demagnification across the Diamond system.
 * The system stores quantities magnified by 1000x in the database/API,
 * but displays normal quantities (1, 2, 3, etc.) in the UI.
 *
 * This is used across vouchers, promos, and other quantity-based features.
 */

// Magnification factor used throughout the system
const QUANTITY_MAGNIFIER = 1000;

/**
 * Magnifies a quantity value for API submission (multiply by 1000)
 * @param value - The user-entered quantity value
 * @returns The magnified quantity for API
 */
export const magnifyQuantity = (
  value: number | null | undefined
): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) {
    return null;
  }
  return value * QUANTITY_MAGNIFIER;
};

/**
 * Demagnifies a quantity value from API response (divide by 1000)
 * @param value - The magnified quantity from API
 * @returns The normal quantity for UI display
 */
export const demagnifyQuantity = (
  value: number | null | undefined
): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) {
    return null;
  }
  return value / QUANTITY_MAGNIFIER;
};

/**
 * Validates if a quantity value is valid after magnification
 * @param value - The user-entered quantity value
 * @returns True if the quantity is valid for magnification
 */
export const isValidQuantityForMagnification = (
  value: number | null | undefined
): boolean => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return false;
  }

  // Check if magnified value would be a valid integer
  const magnified = value * QUANTITY_MAGNIFIER;
  return Number.isInteger(magnified) && magnified > 0;
};

/**
 * Rounds up a value to the nearest multiple.
 * @param value - The value to round
 * @param multiple - The multiple to round up to
 * @returns The rounded up value
 */
export const roundUpToMultiple = (value: number, multiple: number): number => {
  if (multiple <= 0) return value;
  return Math.ceil(value / multiple) * multiple;
};

/**
 * Gets the magnification factor used by the system
 * @returns The quantity magnifier constant (1000)
 */
export const getQuantityMagnifier = (): number => QUANTITY_MAGNIFIER;
